/**
 * LifestyleStep.js — Assessment Wizard, Screen 4 "Lifestyle"
 *
 * Captures daily routine data (FSD Section 4, Screen 4) used for calorie
 * expenditure and meal-timing defaults. Built on the shared
 * AssessmentStepShell so the progress bar, step counter, question header,
 * and Previous/Next footer stay identical to GoalStep and every other
 * wizard screen.
 *
 * Selected-state uses the same 2.4px DIET_C.primary border (no fill) as
 * GoalStep's GoalCard. Next is disabled until all required fields are set
 * (same pattern as GoalStep's `nextDisabled={!selectedGoal}`) rather than
 * clicking through to inline error text.
 *
 * Nutrition Policy v1.0 §16 Hydration: the water intake value captured
 * here is the user's *current* habit, not a generated target — actual
 * fluid targets must not be auto-generated for users with fluid
 * restrictions or kidney/heart conditions (Screen 6, Medical, comes
 * later); that safeguard belongs to the calculation engine, not this
 * screen, but is noted here so the handoff isn't lost.
 *
 * Privacy (Nutrition Policy v1.0 §19 Privacy & Data Minimization): stress
 * level, smoking, alcohol and water intake are health-adjacent answers
 * and are never sent to analytics as raw values — see
 * useLifestyleAnalytics.js for the sanitization.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useLifestyleAnalytics from "./useLifestyleAnalytics";
import { validateLifestyle } from "./LifestyleStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  OCCUPATION_OPTIONS,
  WORK_TYPE_OPTIONS,
  STRESS_LEVELS,
  YES_NO_OPTIONS,
  WATER_INTAKE_MIN,
  WATER_INTAKE_MAX,
  WATER_INTAKE_STEP,
  LIFESTYLE_INITIAL_DATA,
} from "./LifestyleStep.constants";
import {
  FieldGroup,
  FieldRow,
  FieldLabel,
  RequiredMark,
  HelperValue,
  Select,
  TimeInput,
  CardGrid,
  WorkTypeCard,
  RangeInput,
  RangeScaleRow,
  EmojiScaleRow,
  EmojiOption,
  ToggleRow,
  ToggleButton,
  Disclaimer,
} from "./LifestyleStep.styles";

const STEP_NUMBER = STEP_NUMBERS.LIFESTYLE;

const formatWater = (value) => (value >= WATER_INTAKE_MAX ? "5+ L" : `${value} L`);

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {function} props.onPrevious    - Go to Screen 3 (Goal).
 * @param {function} props.onNext        - Go to Screen 5 (Activity Level),
 *   receives the full lifestyle data object.
 */
const LifestyleStep = ({ sessionId, initialData = null, onPrevious, onNext }) => {
  const analytics = useLifestyleAnalytics();
  const [formData, setFormData] = useState({ ...LIFESTYLE_INITIAL_DATA, ...initialData });

  const { isValid, errors } = useMemo(() => validateLifestyle(formData), [formData]);

  const updateField = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      analytics.trackFieldSelect(sessionId, field, value);
    },
    [analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Lifestyle (Screen 4) returns to Goal (Screen 3).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Next is already disabled via nextDisabled below,
      // this only fires if something calls onNext programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    analytics.trackStepNext(sessionId, formData);
    // Advances the wizard to Screen 5 (Activity Level) — the wizard's step
    // router decides which screen renders next, see
    // AssessmentWizard.js's STEP_NUMBERS.LIFESTYLE case.
    onNext?.(formData);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  const showWellnessDisclaimer = formData.smoking === "yes" || formData.alcohol === "yes";

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Your daily lifestyle"
      description="This helps us time your meals and set the right targets."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel htmlFor="occupation">Occupation (optional)</FieldLabel>
        <Select
          id="occupation"
          value={formData.occupation}
          onChange={(e) => updateField("occupation", e.target.value)}
        >
          {OCCUPATION_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>
          Work type
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <CardGrid role="radiogroup" aria-label="Select your work type" $columns={3}>
          {WORK_TYPE_OPTIONS.map((opt) => {
            const active = opt.id === formData.workType;
            return (
              <WorkTypeCard
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                $active={active}
                onClick={() => updateField("workType", opt.id)}
              >
                <i className={`ti ${opt.icon}`} aria-hidden="true" />
                {opt.label}
              </WorkTypeCard>
            );
          })}
        </CardGrid>
      </FieldGroup>

      <FieldRow>
        <div>
          <FieldLabel htmlFor="wakeTime">
            Wake-up time
            <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <TimeInput
            id="wakeTime"
            value={formData.wakeTime}
            $hasError={!formData.wakeTime}
            onChange={(e) => updateField("wakeTime", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel htmlFor="sleepTime">
            Sleep time
            <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <TimeInput
            id="sleepTime"
            value={formData.sleepTime}
            $hasError={!formData.sleepTime}
            onChange={(e) => updateField("sleepTime", e.target.value)}
          />
        </div>
      </FieldRow>

      <FieldGroup>
        <FieldLabel htmlFor="waterIntake">
          Water intake
          <RequiredMark>*</RequiredMark> — <HelperValue>{formatWater(formData.waterIntake)}</HelperValue>
        </FieldLabel>
        <RangeInput
          id="waterIntake"
          min={WATER_INTAKE_MIN}
          max={WATER_INTAKE_MAX}
          step={WATER_INTAKE_STEP}
          value={formData.waterIntake}
          onChange={(e) => updateField("waterIntake", Number(e.target.value))}
        />
        <RangeScaleRow>
          <span>0 L</span>
          <span>5+ L</span>
        </RangeScaleRow>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>
          Stress level
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <EmojiScaleRow role="radiogroup" aria-label="Select your stress level">
          {STRESS_LEVELS.map((level) => {
            const active = formData.stressLevel === level.id;
            return (
              <EmojiOption
                key={level.id}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={level.label}
                $active={active}
                onClick={() => updateField("stressLevel", level.id)}
              >
                {level.emoji}
              </EmojiOption>
            );
          })}
        </EmojiScaleRow>
      </FieldGroup>

      <FieldRow>
        <div>
          <FieldLabel>
            Smoking
            <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <ToggleRow role="radiogroup" aria-label="Smoking">
            {YES_NO_OPTIONS.map((opt) => {
              const active = formData.smoking === opt.id;
              return (
                <ToggleButton
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  $active={active}
                  onClick={() => updateField("smoking", opt.id)}
                >
                  {opt.label}
                </ToggleButton>
              );
            })}
          </ToggleRow>
        </div>
        <div>
          <FieldLabel>
            Alcohol
            <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <ToggleRow role="radiogroup" aria-label="Alcohol">
            {YES_NO_OPTIONS.map((opt) => {
              const active = formData.alcohol === opt.id;
              return (
                <ToggleButton
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  $active={active}
                  onClick={() => updateField("alcohol", opt.id)}
                >
                  {opt.label}
                </ToggleButton>
              );
            })}
          </ToggleRow>
        </div>
      </FieldRow>

      {showWellnessDisclaimer && (
        <Disclaimer>
          A quick note: smoking and alcohol can affect how your body absorbs nutrients. Your
          plan will include a general wellness note about this — general wellness guidance
          only, not a substitute for medical advice, and it doesn't block your assessment.
        </Disclaimer>
      )}
    </AssessmentStepShell>
  );
};

LifestyleStep.displayName = "LifestyleStep";
export default React.memo(LifestyleStep);